import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  RestoreFromTrash as RestoreIcon,
  FilterListOff as FilterListOffIcon,
} from '@mui/icons-material';
import { ConditionPaiement, ConditionPaiementCreate } from '../types';
import { conditionsPaiementAPI } from '../services/api';
import AlertDialog from './AlertDialog';
import useAlert from '../hooks/useAlert';
import { getModuleTableTheme } from '../theme/tableTheme';
import CustomTablePagination from './CustomTablePagination';
import PermissionButton from './PermissionButton';
import ColumnFilter from './ColumnFilter';
import { useColumnFilters } from '../hooks/useColumnFilters';
import { extractErrorMessage } from '../utils/errorHandler';

const CONFIG_COLOR = '#1976D2';

const CONDITION_FILTER_SCHEMA = {
  nom:         { type: 'text', param: 'nom' },
  description: { type: 'text', param: 'description' },
  created_at:  { type: 'dateRange', paramFrom: 'created_from', paramTo: 'created_to' },
} as const;

const ConditionPaiementManagement: React.FC = () => {
  const [conditions, setConditions] = useState<ConditionPaiement[]>([]);
  const [deletedConditions, setDeletedConditions] = useState<ConditionPaiement[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedCondition, setSelectedCondition] = useState<ConditionPaiement | null>(null);
  const [formData, setFormData] = useState<ConditionPaiementCreate>({ nom: '', description: '' });
  const [formLoading, setFormLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { filters, update, reset, hasActive } = useColumnFilters(CONDITION_FILTER_SCHEMA, {
    onChange: () => setPage(0),
  });

  const fetchConditions = async () => {
    try {
      setLoading(true);
      const data = await conditionsPaiementAPI.getAll();
      setConditions(data);
    } catch (err) {
      showError('Erreur lors du chargement des conditions de paiement');
      console.error('Error fetching conditions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeletedConditions = async () => {
    try {
      setLoading(true);
      const data = await conditionsPaiementAPI.getDeleted();
      setDeletedConditions(data);
    } catch (err) {
      showError('Erreur lors du chargement des conditions supprimées');
      console.error('Error fetching deleted conditions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
    if (newValue === 1) {
      fetchDeletedConditions();
    }
  };

  const handleRestore = async (condition: ConditionPaiement) => {
    showConfirm(
      `Êtes-vous sûr de vouloir restaurer la condition "${condition.nom}" ?`,
      async () => {
        try {
          await conditionsPaiementAPI.restore(condition.id);
          showSuccess('Condition de paiement restaurée avec succès');
          fetchDeletedConditions();
          fetchConditions();
        } catch (err: any) {
          showError(extractErrorMessage(err));
        }
      },
      { title: 'Confirmer la restauration', confirmText: 'Restaurer', cancelText: 'Annuler' }
    );
  };

  const handleSubmit = async () => {
    if (!formData.nom.trim()) return;

    try {
      setFormLoading(true);

      if (selectedCondition) {
        await conditionsPaiementAPI.update(selectedCondition.id, formData);
        showSuccess('Condition de paiement modifiée avec succès');
      } else {
        await conditionsPaiementAPI.create(formData);
        showSuccess('Condition de paiement créée avec succès');
      }

      setIsFormOpen(false);
      setSelectedCondition(null);
      setFormData({ nom: '', description: '' });
      fetchConditions();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (condition: ConditionPaiement) => {
    try {
      await conditionsPaiementAPI.delete(condition.id);
      setConditions(conditions.filter(c => c.id !== condition.id));
      showSuccess('Condition de paiement supprimée avec succès');
    } catch (err: any) {
      showError(extractErrorMessage(err));
    }
  };

  const openForm = (condition?: ConditionPaiement) => {
    setSelectedCondition(condition || null);
    setFormData({
      nom: condition?.nom || '',
      description: condition?.description || ''
    });
    setIsFormOpen(true);
  };

  useEffect(() => {
    fetchConditions();
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredConditions = useMemo(
    () =>
      conditions.filter((c) => {
        if (filters.nom && !c.nom.toLowerCase().includes(filters.nom.toLowerCase())) return false;
        if (filters.description && !(c.description || '').toLowerCase().includes(filters.description.toLowerCase())) return false;
        if (filters.created_at.from && new Date(c.created_at) < new Date(filters.created_at.from)) return false;
        if (filters.created_at.to && new Date(c.created_at) > new Date(`${filters.created_at.to}T23:59:59`)) return false;
        return true;
      }),
    [conditions, filters]
  );

  const paginatedConditions = filteredConditions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box p={3} sx={{ width: '100%', maxWidth: 'none' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Gestion des Conditions de Paiement
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {tabValue === 0 && hasActive() && (
            <Button
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={reset}
              sx={{ color: 'text.secondary' }}
            >
              Effacer les filtres
            </Button>
          )}
          {tabValue === 0 && (
            <PermissionButton
              moduleName="Config"
              operation="add"
              miniModuleName="Conditions Paiement"
              variant="contained"
              onClick={() => openForm()}
              startIcon={<AddIcon />}
              sx={{
                backgroundColor: '#1976D2',
                '&:hover': {
                  backgroundColor: '#1565C0',
                }
              }}
            >
              Nouvelle Condition
            </PermissionButton>
          )}
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Conditions Actives" />
          <Tab label="Corbeille" icon={<DeleteIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <>
          <TableContainer component={Paper} sx={getModuleTableTheme('configuration')}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Nom
                      <ColumnFilter type="text" value={filters.nom} onChange={(v) => update('nom', v)} label="nom" color={CONFIG_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Description
                      <ColumnFilter type="text" value={filters.description} onChange={(v) => update('description', v)} label="description" color={CONFIG_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Date de Création
                      <ColumnFilter type="dateRange" value={filters.created_at} onChange={(v) => update('created_at', v)} label="date de création" color={CONFIG_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : filteredConditions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      {hasActive() ? 'Aucune condition ne correspond aux filtres' : 'Aucune condition de paiement trouvée'}
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedConditions.map((condition) => (
                    <TableRow key={condition.id}>
                      <TableCell>{condition.nom}</TableCell>
                      <TableCell>{condition.description || '-'}</TableCell>
                      <TableCell>
                        {new Date(condition.created_at).toLocaleDateString('fr-FR')}
                      </TableCell>
                      <TableCell align="center">
                        <PermissionButton
                          moduleName="Config"
                          operation="edit"
                          miniModuleName="Conditions Paiement"
                          component="IconButton"
                          onClick={() => openForm(condition)}
                        >
                          <EditIcon />
                        </PermissionButton>
                        <PermissionButton
                          moduleName="Config"
                          operation="delete"
                          miniModuleName="Conditions Paiement"
                          component="IconButton"
                          onClick={() => {
                            showConfirm(
                              `Êtes-vous sûr de vouloir supprimer la condition "${condition.nom}" ?`,
                              () => handleDelete(condition),
                              {
                                title: 'Confirmer la suppression',
                                confirmText: 'Supprimer',
                                cancelText: 'Annuler'
                              }
                            );
                          }}
                          color="error"
                        >
                          <DeleteIcon />
                        </PermissionButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={filteredConditions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {tabValue === 1 && (
        <>
          <TableContainer component={Paper} sx={getModuleTableTheme('configuration')}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nom</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Date de Suppression</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : deletedConditions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Aucune condition supprimée
                    </TableCell>
                  </TableRow>
                ) : (
                  deletedConditions
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((condition) => (
                      <TableRow key={condition.id}>
                        <TableCell>{condition.nom}</TableCell>
                        <TableCell>{condition.description || '-'}</TableCell>
                        <TableCell>
                          {condition.deleted_at
                            ? new Date(condition.deleted_at).toLocaleDateString('fr-FR')
                            : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Restaurer">
                            <IconButton
                              onClick={() => handleRestore(condition)}
                              color="primary"
                            >
                              <RestoreIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={deletedConditions.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {/* Form Dialog */}
      <Dialog open={isFormOpen} onClose={() => setIsFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedCondition ? 'Modifier la condition de paiement' : 'Nouvelle condition de paiement'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom"
            fullWidth
            variant="filled"
            value={formData.nom}
            onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
            sx={{ mt: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (optionnel)"
            fullWidth
            variant="filled"
            multiline
            rows={3}
            value={formData.description || ''}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsFormOpen(false)} disabled={formLoading}>
            Annuler
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={formLoading || !formData.nom.trim()}
            sx={{
              backgroundColor: '#4CAF50',
              '&:hover': {
                backgroundColor: '#388E3C',
              }
            }}
          >
            {formLoading ? 'Sauvegarde...' : 'Sauvegarder'}
          </Button>
        </DialogActions>
      </Dialog>

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onClose={hideAlert}
        onConfirm={alertState.onConfirm}
        confirmText={alertState.confirmText}
        cancelText={alertState.cancelText}
        showCancel={alertState.showCancel}
      />
    </Box>
  );
};

export default ConditionPaiementManagement;
